<template>
    <div class="station-container" :class="service.class">
        <div class="station-infos-wrapper">
            <div class="route-infos">
                <div class="first-line">
                    <div class="service-icon">
                        <span class="visuallyhidden"> {{ t('route_service') }} {{ service.name }} </span>
                        <img :src="service.icon" alt="" />
                    </div>
                    <h2 class="route-number">{{ route.noParcours }}</h2>
                    <span v-if="route.accessible" class="accessible-icon">
                        <span class="visuallyhidden">{{ t('accessible') }}</span>
                        <img src="@@/img/accessible.svg" alt="" />
                    </span>
                    <span v-else class="visuallyhidden">{{ t('not_accessible') }}</span>
                    <span v-if="hasNotices">
                        <span class="visuallyhidden">{{ t('route_with_notices') }}</span>
                        <img src="@@/img/alert.svg" alt="" />
                    </span>
                </div>
                <div class="stop-content-wrapper">
                    <slot name="stop-content">
                        <h3 class="direction-title">{{ t('to') }} {{ route.descriptionDirection }}</h3>
                    </slot>
                </div>
            </div>
            <div v-if="ntrEnabled === false" class="next-departures">
                <button type="button" class="btn-refresh" @click="$emit('refresh')">
                    <IconRefresh width="24" height="24" />
                    <span>{{ t('reenable_ntr') }}</span>
                </button>
            </div>
            <template v-else-if="isToday && station">
                <ul
                    v-if="
                        station.horaires &&
                        station.horaires.length &&
                        !station.descenteSeulement &&
                        !station.arretNonDesservi
                    "
                    class="next-departures station no-bullet"
                >
                    <li v-for="(departure, i) in station.horaires.slice(0, 3)" :key="i">
                        <RealTimeItem :departure="departure" />
                    </li>
                </ul>
                <div v-else class="no-departures">
                    <template v-if="station.arretNonDesservi">
                        {{ t('unserved_stop') }}
                    </template>
                    <template v-else-if="station.descenteSeulement">
                        {{ t('drop_off_only') }}
                    </template>
                    <template v-else>
                        {{ t('service_over') }}
                    </template>
                </div>
            </template>
        </div>
        <slot name="additional-content"></slot>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useSchedulesStore } from '@@/js/stores/schedules';
import RealTimeItem from '@/shared/RealTimeItem';

import IconRefresh from '@@/icons/refresh.svg';

const props = defineProps({
    station: {
        type: Object,
        default: () => ({}),
    },
    route: {
        type: Object,
        default: () => ({}),
    },
    isToday: {
        type: Boolean,
        default: false,
    },
    hasNotices: {
        type: Boolean,
        default: false,
    },
    ntrEnabled: {
        type: Boolean,
        default: null,
    },
});

const schedulesStore = useSchedulesStore();
const { getServiceType } = schedulesStore;
const service = computed(() => getServiceType(props.route.codeTypeService));

const { t } = useI18n({
    messages: {
        fr: {
            route_service: 'Parcours du service',
            route_with_notices: 'Parcours comportant des avis',
            to: 'Vers',
            unserved_stop: 'Arrêt non desservi',
            drop_off_only: 'Descente seulement',
            service_over: 'Service terminé',
            reenable_ntr: 'Réactiver le temps réel',
            accessible: 'Parcours accessible',
            not_accessible: 'Parcours non accessible',
        },
        en: {
            route_service: 'Route of service',
            route_with_notices: 'Route with notices',
            to: 'To',
            canceled_departure: 'Cancelled departure',
            drop_off_only: 'Drop off only',
            service_over: 'No more buses',
            reenable_ntr: 'Re-enable real time',
            accessible: 'Accessible route',
            not_accessible: 'Not accessible route',
        },
    },
});
</script>

<style lang="scss" scoped>
.station-container {
    display: flex;
    justify-content: space-between;
    padding: 16px;
    color: $color-white;
    flex-wrap: wrap;
    border-radius: 20px;
    border: 4px solid $color-white;
    @include colorize {
        background-color: get-color-variation('base');
    }
    @include breakpoint(medium) {
        flex-wrap: nowrap;
    }
}
.station-infos-wrapper {
    display: flex;
    width: 100%;
    gap: 16px;
}
.route-number {
    @include rem-typo(36px);
    margin: 0 16px;
    font-weight: 800;
    @include breakpoint(medium) {
        @include rem-typo(42px);
    }
}
.service-icon {
    width: 32px;
    height: 32px;
    @include breakpoint(medium) {
        width: 40px;
        height: 40px;
    }
}
.accessible-icon {
    margin-right: 16px;
    width: 24px;
    height: 24px;
}
.route-infos {
    flex-grow: 1;
}
.first-line {
    display: flex;
    align-items: center;
}
.no-departures,
.next-departures {
    display: flex;
    height: 100px;
    margin: 0 auto;
    text-align: center;
    border-left: 1px solid $color-white;
    flex: 0 0 85px;
}
.next-departures {
    align-items: center;
    justify-content: space-around;
}
.station {
    flex-direction: column;
    align-self: stretch;
}
.no-departures {
    @include rem-typo(14px);
    align-items: center;
    line-height: 1.1;
}
.stop-content-wrapper {
    @include breakpoint(medium) {
        margin-left: 56px;
    }
}
:deep(.direction-title) {
    @include rem-typo(16px);
    display: inline-block;
    font-weight: 600;
    margin: 0;
    @include breakpoint(medium) {
        @include rem-typo(20px);
    }
}
.btn-refresh {
    @include rem-typo(14px);
    padding: 8px;
    color: $color-white;
    background: transparent;
    border: 0;
    svg {
        margin-bottom: 4px;
        fill: $color-white;
    }
}
</style>
